/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, October 20th 2021, 8:43:25 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { schemaBuilder, Many2ManyIntPKBaseSchema, validateManyAtLeastOne } from "lib/validation";
import { WritableModel } from "lib/schemas/abstract";
import { Portal } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import { RegistrationProfile, RegistrationProfileValidationSchema } from "./RegistrationProfile";

export enum USER_TYPES {
  CLIENT = "CLIENT",
  ADMINISTRATOR = "ADMINISTRATOR",
  WORKER = "WORKER",
}

export const STAFF_USER_TYPES: Omit<typeof USER_TYPES, "CLIENT"> = Object.keys(USER_TYPES)
  .filter((item) => item !== USER_TYPES.CLIENT)
  .reduce((prev, curr) => {
    return { ...prev, ...{ [curr]: USER_TYPES[curr] } };
  }, {}) as any;

export const STAFF_USER_TYPES_DESCRIPTIONS = {
  [STAFF_USER_TYPES.ADMINISTRATOR]:
    "Has access to admin console and staff portals. Can create and edit content, manage users, manage security & manage billing",
  [STAFF_USER_TYPES.WORKER]:
    "Has access to staff portals only. Will be able to carry out tasks on the staff portals based on team permissions you assign them.",
};

export interface User extends WritableModel<false> {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  readonly is_active?: boolean;
  readonly last_login?: Date;
  sites: PrimaryKeyValue<Portal>[];
  registrationprofile: RegistrationProfile;
  readonly profile_name?: string;
  readonly has_federated_account?: boolean;
  readonly is_superuser?: boolean;
}

export interface WorkerAccount extends User {
  readonly worker_type?: keyof Omit<typeof USER_TYPES, "CLIENT">;
  readonly is_super_user?: boolean;
}

type WriteOnlyUser = Omit<User, "profile_name" | "has_federated_account" | "is_superuser">;

export const UserValidationSchema: schemaBuilder.SchemaOf<WriteOnlyUser> = schemaBuilder.object().shape({
  id: schemaBuilder.number().nullable(),
  username: schemaBuilder.string().required("UserName is required."),
  email: schemaBuilder.string().email().required("Email is required."),
  first_name: schemaBuilder.string().nullable(),
  last_name: schemaBuilder.string().nullable(),
  is_active: schemaBuilder.boolean(),
  last_login: schemaBuilder.date().nullable(),
  sites: Many2ManyIntPKBaseSchema().test(
    "at-least-one-portal",
    "You must assign at least one portal to the worker.",
    validateManyAtLeastOne
  ),
  registrationprofile: RegistrationProfileValidationSchema.required("Registration profile is required"),
});

export interface UserLoginCredentials {
  username: string;
  password: string;
}

export const UserLoginCredentialsValidationSchema = schemaBuilder.object({
  username: schemaBuilder.string().required("Please enter your Username or Email Address."),
  password: schemaBuilder.string().required("Please enter your password."),
});
